/**
 * A filter panel's state carried on the URL under `filters[<scope>]…` — the round trip
 * between the router's flat query and the nested object a draft holds.
 *
 * `parseScopedFiltersQuery` reads one scope out of `route.query`,
 * `flattenScopedFiltersToQuery` writes it back, and `removeScopedFilterKeys` strips the
 * scope first so a filter that was cleared actually leaves the URL. The scope is what stops
 * two panels on one page from reading each other's keys.
 *
 * Everything comes back as a **string** or an array of strings, since that is all a query
 * string carries — build a panel's options with string values, or its controls cannot match
 * their own state. Runs of numeric keys (`[0]`, `[1]`) become arrays again.
 *
 * `pickSignificantFilters` and `hasActiveFilters` are the other half: what is worth
 * sending, and whether an empty list means "nothing matches" or "nothing exists". Reach
 * into this module directly only when building a filter mechanism — a page uses
 * `useFilters` and `useFilterPanel`, which are written over it.
 */

import type { LocationQuery, LocationQueryValue } from 'vue-router'

import { isPlainObject, type PlainObject } from './plainObject'

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeQueryValue(
  v: LocationQueryValue | LocationQueryValue[] | undefined
): string | string[] {
  if (Array.isArray(v)) return v.map((x) => String(x ?? ''))
  return v == null ? '' : String(v)
}

function parseBracketSegments(key: string): string[] | null {
  const matches = key.match(/\[([^\]]+)\]/g)
  if (!matches) return null
  return matches.map((m) => m.slice(1, -1))
}

function buildScopedKey(scope: string, path: string[]): string {
  return `filters[${scope}]` + path.map((p) => `[${p}]`).join('')
}

function setDeep(obj: PlainObject, path: string[], value: unknown): void {
  let cur = obj

  for (const key of path.slice(0, -1)) {
    if (!isPlainObject(cur[key])) cur[key] = {}
    cur = cur[key] as PlainObject
  }

  const leaf = path[path.length - 1]
  // Every caller passes a non-empty path (`parseBracketSegments` returns `null` rather
  // than `[]`), but the compiler reads the last index as possibly absent.
  if (leaf === undefined) return

  cur[leaf] = value
}

/**
 * The largest index a filter array may carry. The keys come off the address bar and the
 * loop below runs to the highest of them, so `?filters[x][y][9999999]=1` would build a
 * ten-million-entry array before the page renders. A multi-select never comes near it, and
 * a run of keys past it simply stays the object it already is — which still round-trips,
 * since `flattenScopedFiltersToQuery` writes an object's keys the same way.
 */
const MAX_FILTER_ARRAY_INDEX = 1000

/**
 * A run of numeric keys read back as an array, or `null` when this object is not one.
 *
 * At least one key, every one of them a numeric index. An empty object must **not** pass:
 * `[].every()` is vacuously true, and `Math.max()` of nothing is -Infinity, which hangs
 * the loop.
 */
function asNumericArray(node: PlainObject): unknown[] | null {
  const keys = Object.keys(node)

  if (!keys.length || !keys.every((k) => /^\d+$/.test(k))) return null

  // Reduced, not spread: `Math.max(...keys)` overflows the argument list on a long run,
  // which happens before the bound below gets to refuse it.
  const max = keys.reduce((top, key) => Math.max(top, Number(key)), 0)
  if (max > MAX_FILTER_ARRAY_INDEX) return null

  const values: unknown[] = []

  for (let i = 0; i <= max; i++) {
    const value = node[String(i)]
    values.push(value !== undefined ? convertNumericObjects(value) : undefined)
  }

  while (values.length > 0 && values[values.length - 1] === undefined)
    values.pop()

  return values
}

function convertNumericObjects(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(convertNumericObjects)
  if (!isPlainObject(node)) return node

  const asArray = asNumericArray(node)
  if (asArray) return asArray

  const out: PlainObject = {}

  for (const key of Object.keys(node)) {
    out[key] = convertNumericObjects(node[key])
  }

  return out
}

export function parseScopedFiltersQuery(
  query: LocationQuery,
  scope: string
): PlainObject {
  const prefix = `filters[${scope}]`
  const root: PlainObject = {}

  for (const [key, value] of Object.entries(query)) {
    if (!key.startsWith(prefix)) continue

    const segments = parseBracketSegments(key.slice(prefix.length))
    if (!segments?.length) continue

    setDeep(root, segments, normalizeQueryValue(value))
  }

  const result = convertNumericObjects(root)

  return isPlainObject(result) ? result : {}
}

export function flattenScopedFiltersToQuery(
  scope: string,
  value: PlainObject
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {}

  function walk(path: string[], v: unknown): void {
    if (v == null) return

    if (typeof v === 'string') {
      if (v !== '') out[buildScopedKey(scope, path)] = v
      return
    }

    if (typeof v === 'number' || typeof v === 'boolean') {
      out[buildScopedKey(scope, path)] = String(v)
      return
    }

    if (Array.isArray(v)) {
      v.forEach((item, i) => walk([...path, String(i)], item))
      return
    }

    if (isPlainObject(v)) {
      for (const [k, val] of Object.entries(v)) {
        walk([...path, k], val)
      }
    }
  }

  walk([], value)

  return out
}

export function removeScopedFilterKeys(
  query: LocationQuery,
  scope: string
): LocationQuery {
  const pattern = new RegExp(`^filters\\[${escapeRegExp(scope)}\\]`)

  return Object.fromEntries(
    Object.entries(query).filter(([k]) => !pattern.test(k))
  )
}

export function deepMergeFilters(
  base: PlainObject,
  patch: PlainObject
): PlainObject {
  const out: PlainObject = { ...base }

  for (const [k, pv] of Object.entries(patch)) {
    const bv = base[k]

    if (isPlainObject(pv) && isPlainObject(bv)) {
      out[k] = deepMergeFilters(bv, pv)
    } else {
      out[k] = pv
    }
  }

  return out
}

export function hasSignificantFilterValues(value: unknown): boolean {
  if (value == null) return false

  if (typeof value === 'string') return value.length > 0
  if (typeof value === 'number' || typeof value === 'boolean') return true

  if (Array.isArray(value)) {
    return value.some(hasSignificantFilterValues)
  }

  if (isPlainObject(value)) {
    return Object.values(value).some(hasSignificantFilterValues)
  }

  return false
}

/**
 * Drop empty entries (null, '', empty arrays/objects) from a flat filter map,
 * so only active filters reach the list request and its query key.
 */
export function pickSignificantFilters(
  filters: PlainObject
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) =>
      hasSignificantFilterValues(value)
    )
  )
}

/**
 * Whether a compacted filter narrows the list at all — which is what decides the wording of
 * an empty result: a table shows its empty state ("nothing exists") only when nothing was
 * filtered, and a plain empty grid ("nothing matches") otherwise.
 */
export function hasActiveFilters(filters: PlainObject | undefined): boolean {
  return !!filters && Object.keys(filters).length > 0
}
